/**
 * As ferramentas do diagrama — o menu da barra do `Editar` do Sistema de hoje.
 *
 * Lá são itens de um dropdown; aqui são cartões, porque cada um precisa dizer o
 * que faz **antes** de ser apertado: dois deles apagam conteúdo e um grava a
 * coluna que o site do cliente serve.
 *
 * Ficaram de fora, de propósito, os que já vivem melhor em outro lugar: cor e
 * desenho interno do componente principal e vínculo com outro diagrama estão na
 * ficha; verificar está na aba do desenho; incluir ligações em lote está lá
 * também.
 */

import { useState } from "react"
import {
  usarAplicarModelos, usarAplicarPosicionamentos, usarCalcularMargens,
  usarConferirModelos, usarIncluirComponenteAvulso, usarPosicionamentosDoSistema,
  usarRedesenharCadeia,
} from "@/api/ferramentasDoDiagrama"
import { usarComponentes } from "@/api/catalogos"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"
import { BarraLateral } from "@/kit/LarguraArrastavel"
import { LigacoesEmLote } from "./LigacoesEmLote"

function mensagem(e: unknown): string {
  if (e instanceof ErroDeTrava) return e.message
  if (e instanceof ErroDaApi) return e.message
  return e instanceof Error ? e.message : "Não foi possível executar."
}

const entrada: React.CSSProperties = {
  padding: "4px 8px", fontSize: 11.5, border: "1px solid var(--line)",
  borderRadius: 5, background: "var(--surface)", color: "var(--txt)",
}

function Cartao({ titulo, explica, children }: {
  titulo: string; explica: React.ReactNode; children: React.ReactNode
}) {
  return (
    <div style={{ padding: "9px 11px", borderRadius: 8, background: "var(--surface2)",
                  display: "flex", flexDirection: "column", gap: 7 }}>
      <b style={{ fontSize: 12 }}>{titulo}</b>
      <span style={{ fontSize: 10.5, color: "var(--txt3)", lineHeight: 1.55 }}>{explica}</span>
      <div style={{ display: "flex", gap: 7, flexWrap: "wrap", alignItems: "center" }}>{children}</div>
    </div>
  )
}

export function FerramentasDoDiagrama({ projetoId, diagramaId, sistemaId, componentes, aoFechar, aoMudar }: {
  projetoId: number
  diagramaId: number
  sistemaId: number
  /** Os componentes do diagrama, para os pares do posicionamento. */
  componentes: { id: number; codigo: string; descricao: string }[]
  aoFechar: () => void
  aoMudar: (recado: string) => void
}) {
  const margens = usarCalcularMargens(projetoId, diagramaId)
  const cadeia = usarRedesenharCadeia(projetoId, diagramaId)
  const avulso = usarIncluirComponenteAvulso(projetoId, diagramaId)
  const aplicarModelos = usarAplicarModelos(projetoId, diagramaId)
  const aplicarPosicoes = usarAplicarPosicionamentos(projetoId, diagramaId)

  const [conferindoModelos, setConferindoModelos] = useState(false)
  const conferencia = usarConferirModelos(diagramaId, conferindoModelos)

  const catalogo = usarComponentes(projetoId)
  const [novoComponente, setNovoComponente] = useState(0)
  const [incluindoLigacoes, setIncluindoLigacoes] = useState(false)

  const posicionamentos = usarPosicionamentosDoSistema(sistemaId, true)
  const [pares, setPares] = useState<Record<number, number>>({})

  return (
    <BarraLateral chave="ferramentas-do-diagrama" padrao={560} rotulo="Ferramentas do diagrama">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <b style={{ fontSize: 13 }}>Ferramentas do diagrama {diagramaId}</b>
          <button onClick={aoFechar} style={{ ...entrada, marginLeft: "auto", cursor: "pointer" }}>
            fechar
          </button>
        </div>

        <Cartao
          titulo="Calcular a margem pelo componente"
          explica={<>
            Dá a cada componente o espaço que o texto dele precisa embaixo. <b>Só aumenta</b> —
            rodar de novo ajusta zero, e isso é sinal de que está certo.
          </>}>
          <button
            onClick={() => margens.mutate(undefined, {
              onSuccess: (r) => aoMudar(
                `Margens: ${r.ajustados} de ${r.componentes} componentes ajustados.`),
              onError: (e) => aoMudar(mensagem(e)),
            })}
            disabled={margens.isPending}
            style={{ ...entrada, cursor: "pointer", fontWeight: 600 }}>
            {margens.isPending ? "Calculando…" : "Calcular margens"}
          </button>
        </Cartao>

        <Cartao
          titulo="Incluir componente sem ligação"
          explica={<>
            Para o componente que precisa aparecer no desenho mas não se liga a nada do pinout —
            um aterramento de carroceria, uma emenda. Entra à direita, no fim.
          </>}>
          <select value={novoComponente} onChange={(e) => setNovoComponente(Number(e.target.value))}
                  style={{ ...entrada, width: 280 }}>
            <option value={0}>
              {catalogo.carregando ? "carregando…" : "escolha o componente"}
            </option>
            {catalogo.itens.map((c) => (
              <option key={c.id} value={c.id}>{c.codigo} {c.rotulo}</option>
            ))}
          </select>
          <button
            disabled={novoComponente === 0 || avulso.isPending}
            onClick={() => avulso.mutate(novoComponente, {
              onSuccess: () => { setNovoComponente(0); aoMudar("Componente incluído, sem ligação.") },
              onError: (e) => aoMudar(mensagem(e)),
            })}
            style={{ ...entrada, cursor: "pointer", opacity: novoComponente === 0 ? 0.5 : 1 }}>
            {avulso.isPending ? "Incluindo…" : "Incluir"}
          </button>
        </Cartao>

        {/* Veio do painel da lista de diagramas, onde a produção não via sentido nela:
            incluir ligações é trabalho de montagem. Abre sob pedido — a lista de
            candidatos é montada pelo motor e não vale pedir a cada abertura. */}
        <Cartao
          titulo="Incluir ligações"
          explica={<>
            Os pinos do pinout que ainda não estão no diagrama, com o componente de cada um. Entra
            só a linha marcada — aterramento e fusível, cada um no seu componente.
          </>}>
          {incluindoLigacoes ? (
            <div style={{ flexBasis: "100%" }}>
              <LigacoesEmLote projetoId={projetoId} diagramaId={diagramaId}
                              aoIncluir={() => aoMudar("Ligações incluídas.")} />
            </div>
          ) : (
            <button onClick={() => setIncluindoLigacoes(true)}
                    style={{ ...entrada, cursor: "pointer", fontWeight: 600 }}>
              Ver o que falta incluir
            </button>
          )}
        </Cartao>

        <Cartao
          titulo="Redesenhar incluindo o vínculo acima"
          explica={<>
            Dois diagramas ligados são um desenho só: sobe o vínculo até o topo, desenha de lá e
            grava o mesmo traço nos dois. <b>Grava a coluna que o site do cliente serve.</b>
          </>}>
          <button
            onClick={() => cadeia.mutate(undefined, {
              onSuccess: (r) => aoMudar(
                r.elos === 0
                  ? `Redesenhado (sem vínculo acima), ${(r.milissegundos / 1000).toFixed(1)} s.`
                  : `Redesenhado do diagrama ${r.diagramaDoTopo}, ${r.elos} elo(s) acima.`),
              onError: (e) => aoMudar(mensagem(e)),
            })}
            disabled={cadeia.isPending}
            style={{ ...entrada, cursor: "pointer", fontWeight: 600 }}>
            {cadeia.isPending ? "Desenhando…" : "Redesenhar a cadeia"}
          </button>
        </Cartao>

        <Cartao
          titulo="Conferir pinout × modelos"
          explica={<>
            Acha os terminais que não servem a algum modelo do diagrama. A conferência separa o
            que o sistema antigo junta: <b>órfão</b> (não serve a modelo nenhum) e{" "}
            <b>de outro modelo</b> (serve a outro modelo deste diagrama, e está certo onde está).
          </>}>
          {!conferindoModelos ? (
            <button onClick={() => setConferindoModelos(true)} style={{ ...entrada, cursor: "pointer" }}>
              Conferir
            </button>
          ) : conferencia.isPending ? (
            <span style={{ fontSize: 11.5, color: "var(--txt3)" }}>Conferindo…</span>
          ) : conferencia.error ? (
            <span style={{ fontSize: 11.5, color: "var(--erro)" }}>{mensagem(conferencia.error)}</span>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%" }}>
              <span style={{ fontSize: 11.5 }}>
                modelos {conferencia.data?.modelos.join(", ") || "—"} ·{" "}
                <b>{conferencia.data?.orfaos ?? 0} órfão(s)</b> ·{" "}
                {conferencia.data?.deOutroModelo ?? 0} de outro modelo
              </span>
              {(conferencia.data?.terminais.length ?? 0) > 0 && (
                <div style={{ maxHeight: 140, overflow: "auto", fontSize: 10.5,
                              display: "flex", flexDirection: "column", gap: 2 }}>
                  {conferencia.data?.terminais.slice(0, 40).map((t) => (
                    <span key={`${t.diagramaLigacaoId}-${t.modeloId}`}
                          style={{ color: t.serveOutroModelo ? "var(--txt3)" : "var(--txt)" }}>
                      {t.serveOutroModelo ? "· " : "! "}
                      {t.componenteDescricao} · pino {t.pino} · {t.circuitoDescricao}
                      {t.serveOutroModelo ? " (serve a outro modelo)" : " (órfão)"}
                    </span>
                  ))}
                </div>
              )}
              <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                <button
                  disabled={(conferencia.data?.orfaos ?? 0) === 0 || aplicarModelos.isPending}
                  onClick={() => aplicarModelos.mutate(false, {
                    onSuccess: (r) => aoMudar(
                      `${r.ligacoesRemovidas} terminal(is) órfão(s) apagado(s), ` +
                      `${r.componentesRemovidos} componente(s) para a lixeira.`),
                    onError: (e) => aoMudar(mensagem(e)),
                  })}
                  style={{ ...entrada, cursor: "pointer", color: "var(--erro)" }}>
                  Apagar só os órfãos
                </button>
                <button onClick={() => setConferindoModelos(false)} style={entrada}>Fechar</button>
              </div>
              <span style={{ fontSize: 10, color: "var(--txt3)" }}>
                O "Excluir" do sistema antigo apagaria os dois grupos juntos — nos diagramas
                publicados de hoje, 90% do que ele removeria serve a outro modelo.
              </span>
            </div>
          )}
        </Cartao>

        <Cartao
          titulo="Posicionamentos padrão"
          explica={<>
            Aplica um padrão do sistema ao componente: <b>apelido, ordem + lado e cor</b> de uma
            vez. Cuidado com o apelido — ele é do componente <b>no projeto</b>, então muda em todo
            diagrama que o usa.
          </>}>
          {sistemaId === 0 ? (
            <span style={{ fontSize: 11, color: "var(--txt3)" }}>
              Este diagrama não tem sistema. Defina o sistema na aba Ficha para usar os padrões.
            </span>
          ) : (posicionamentos.data ?? []).length === 0 ? (
            <span style={{ fontSize: 11, color: "var(--txt3)" }}>
              O sistema {sistemaId} não tem posicionamento padrão cadastrado.
            </span>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, width: "100%" }}>
              <div style={{ maxHeight: 200, overflow: "auto", display: "flex",
                            flexDirection: "column", gap: 3 }}>
                {componentes.map((c) => (
                  <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 7,
                                           fontSize: 11 }}>
                    <span style={{ minWidth: 74, fontWeight: 600 }}>{c.codigo}</span>
                    <span style={{ flex: 1, minWidth: 0, color: "var(--txt3)", overflow: "hidden",
                                   textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {c.descricao}
                    </span>
                    <select
                      value={pares[c.id] ?? 0}
                      onChange={(e) => setPares({ ...pares, [c.id]: Number(e.target.value) })}
                      style={{ ...entrada, width: 190 }}>
                      <option value={0}>—</option>
                      {(posicionamentos.data ?? []).map((pp) => (
                        <option key={pp.id} value={pp.id}>
                          {pp.apelido} · {pp.posicao} {pp.ordem}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
              <button
                disabled={aplicarPosicoes.isPending ||
                          Object.values(pares).filter((v) => v > 0).length === 0}
                onClick={() => aplicarPosicoes.mutate(
                  Object.entries(pares)
                    .filter(([, pp]) => pp > 0)
                    .map(([dc, pp]) => ({ diagramaComponenteId: Number(dc), posicionamentoPadraoId: pp })),
                  {
                    onSuccess: (r) => { setPares({}); aoMudar(
                      `${r.aplicados} posicionamento(s) aplicado(s), ${r.apelidos} apelido(s) trocado(s).`) },
                    onError: (e) => aoMudar(mensagem(e)),
                  })}
                style={{ ...entrada, cursor: "pointer", fontWeight: 600, alignSelf: "flex-start" }}>
                {aplicarPosicoes.isPending ? "Aplicando…" : "Aplicar os escolhidos"}
              </button>
            </div>
          )}
        </Cartao>
    </BarraLateral>
  )
}
